import { Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { useController } from "react-hook-form";
import { getNestedValue } from "../../helpers";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function Select({ options, label, control, name, inputSize = "xl", rules, disabled = false }) {
  const {
    field,
    formState: { errors },
  } = useController({ name, control, rules });

  const errorForField = getNestedValue(errors, name);

  // Optional sizing classes
  const sizeClasses = {
    sm: "py-1 text-sm",
    md: "py-1.5 text-sm",
    lg: "py-2 text-base",
    xl: "py-3 text-base",
  };

  return (
    <>
      <Listbox value={field.value} onChange={field.onChange} disabled={disabled}>
        {({ open }) => (
          <>
            <Listbox.Label className="block text-sm font-light leading-6 text-gray-900">
              {label}
            </Listbox.Label>

            <div className="relative mt-2">
              <Listbox.Button
                data-testid={name ? `${name}-select-button` : undefined}
                className={classNames(
                  errorForField
                    ? "text-red-900 ring-red-300 focus:ring-red-600"
                    : "text-gray-900 ring-gray-200 focus:ring-blue",
                  "relative w-full cursor-default rounded-md bg-white pl-3 pr-10 ring-1 ring-inset focus:outline-none focus:ring-2 sm:leading-6",
                  sizeClasses[inputSize] || sizeClasses.md
                )}
              >
                <span className="flex items-center">
                  {field.value?.icon && (
                    <span className="flex-shrink-0 mr-3">{field.value.icon}</span>
                  )}
                  <span className="block truncate min-h-[22.5px]">
                    {field.value?.name}
                  </span>
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </span>
              </Listbox.Button>

              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {options?.map((o, id) => (
                    <Listbox.Option
                      key={id}
                      className={({ active }) =>
                        classNames(
                          active ? "bg-blue text-white" : "text-gray-900",
                          "cursor-default select-none py-2 pl-3 pr-9"
                        )
                      }
                      value={o}
                    >
                      {({ selected, active }) => (
                        <>
                          <div className="flex items-center">
                            {o.icon && <span className="flex-shrink-0 mr-3">{o.icon}</span>}
                            <span className={classNames(selected ? "font-semibold" : "font-normal", "block truncate")}>
                              {o.name}
                            </span>
                          </div>
                          {selected && (
                            <span
                              className={classNames(
                                active ? "text-white" : "text-blue",
                                "absolute inset-y-0 right-0 flex items-center pr-4"
                              )}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>

      {errorForField && (
        <p className="mt-2 text-sm text-red-600" id={`${name}-error`}>
          {errorForField.message}
        </p>
      )}
    </>
  );
}